/* ============================================================
   MOSAIK — APP SHELL

   Le châssis autour du plan, LE MÊME SUR TOUS LES ÉCRANS :

     #mosaik-stage      the plan
     #mosaik-sidebar    the controls (collapsible cards)
     #mosaik-actionbar  save / details / PDF / total / cart
     #mosaik-details    drawer holding the recap table

   Seule la MISE EN PAGE change avec la taille de l'écran, et elle est
   entièrement portée par le CSS du template :
     ≥1025px  deux panneaux côte à côte, chacun avec son propre défilement —
              le shell prend une hauteur fixe (calculée ici) pour ça ;
     ≤1024px  tout empilé, la barre d'action collée en bas de l'écran.
   Ce module ne connaît pas cette différence : il câble les mêmes éléments
   dans les deux cas (l'ancienne barre d'outils mobile, qui doublait cette
   interface avec ses propres popovers, a été supprimée).

   No painting logic lives here. The palettes are still built by
   mosaik-controller.js; this module wires the chrome around them.
   ============================================================ */
window.MosaikUI = {

  m: null,
  exporting: false,          // set by mosaik-pdf.js while it borrows the DOM
  _lastBox: null,
  _t: null,

  $:function(id){ return document.getElementById(id); },
  isSplit:function(){ return window.matchMedia('(min-width:1025px)').matches; },
  isMobile:function(){ return window.matchMedia('(max-width:768px)').matches; },

  /* ---------- layout ----------
     Called BEFORE the first draw so the plan is sized against the real pane.
     The shell fills what is left of the viewport under the theme header: we
     measure the shell's offset from the top of the DOCUMENT (scroll-independent,
     unlike getBoundingClientRect().top alone) and subtract it. Capped at half
     the viewport so a tall header can never squeeze the shell to nothing. */
  setLayout:function(){
    const shell = this.$('mosaik-shell'), conf = this.$('mosaik-conf');
    if(!shell || !conf) return;
    // AVANT de mesurer : la navigation escamotée rend sa hauteur au shell, et
    // c'est cette hauteur-là qu'on est en train de calculer (setupNavAway).
    this.setupNavAway();
    const docTop = shell.getBoundingClientRect().top + (window.pageYOffset || 0);
    const top = Math.min(Math.max(docTop, 0), window.innerHeight * 0.5);
    /* Le plancher de 560 px est celui du BUREAU : deux panneaux côte à côte ne
       valent rien plus courts, et la page peut défiler pour les montrer. Sur un
       téléphone le shell est au contraire un PARTAGE EXACT de l'écran (plan /
       réglages / barre) : lui imposer 560 px sur un appareil qui n'en offre que
       500 rendrait la page défilante, c'est-à-dire exactement ce que ce partage
       supprime. Le plancher n'y sert plus que de garde-fou pour un en-tête de
       thème démesuré. */
    const floor = this.isSplit() ? 560 : 360;
    const h = Math.max(floor, Math.round(window.innerHeight - top - 16));
    conf.style.setProperty('--mos-shell-h', h + 'px');
    // the details drawer sits just above the action bar, whose height depends on
    // the theme's font sizes — measure it rather than guessing.
    const bar = this.$('mosaik-actionbar');
    if(bar && bar.offsetHeight) conf.style.setProperty('--mos-bar-h', bar.offsetHeight + 'px');
  },

  /* Redraw when the pane the plan lives in actually changed size. Debounced,
     and guarded by a size threshold so a scrollbar appearing (which nudges
     clientWidth) can never bounce between two layouts.

     The watched box is the STAGE, not #mosaik-canvas: the frame's height now
     follows the plan (a long floor scrolls the card instead of being clipped),
     so observing it would feed every redraw straight back into itself. The
     stage is exactly what drawBox() derives both axes from. */
  _watchResize:function(){
    const self = this;
    let hard = false;                    // vrai changement de fenêtre, pas le zoom
    const bump = (isHard) => {
      if(isHard) hard = true;
      clearTimeout(self._t);
      self._t = setTimeout(() => {
        if(self.exporting) return;
        /* À la souris, le zoom fait grandir le cadre du plan (mosaik-zoom.js,
           _dFrameHeight), donc la scène : c'est le zoom qui redimensionne, pas
           la fenêtre.
           Redessiner ici remettrait le plan à 1:1 juste après le pincement du
           visiteur. Un vrai resize de fenêtre, lui, passe — la place disponible
           a réellement changé, et il est normal d'y repartir du plan entier. */
        if(!hard && window.MosaikZoom && MosaikZoom.zoomed()) return;
        hard = false;
        self.setLayout();
        const box = self.$('mosaik-stage');
        if(box){
          const b = box.clientWidth + 'x' + box.clientHeight;
          if(b === self._lastBox) return;
          self._lastBox = b;
        }
        // the plan is about to be re-drawn at a new size: any zoom/pan in flight
        // is anchored to the old one, so drop it first
        if(window.MosaikZoom) MosaikZoom.reset();
        if(self.m) self.m.draw();
      }, 160);
    };
    window.addEventListener('resize', () => bump(true));
    const box = this.$('mosaik-stage');
    if(box && window.ResizeObserver) new ResizeObserver(() => bump(false)).observe(box);
  },

  /* ---------- wiring ---------- */
  setupUI:function(mosaik){
    const shell = this.$('mosaik-shell');
    if(!shell) return;
    this.m = mosaik;
    const self = this, t = s => mosaik.getText(s);

    // Collapsible sidebar cards — chacune se replie et se déplie pour elle-même.
    // « Dalles » et « Bordures et angles » s'ouvraient autrefois de façon
    // exclusive (ouvrir l'une repliait l'autre) ; elles sont désormais
    // indépendantes, comme toutes les autres cartes.
    document.querySelectorAll('#mosaik-sidebar [data-collapse]').forEach(head => {
      head.addEventListener('click', () => {
        const id = head.getAttribute('data-collapse');
        const card = self.$(id);
        if(!card) return;
        card.classList.toggle('collapsed');
        // « Bordures et angles » commande AUSSI le plan : la délimitation des
        // cases de bordure et d'angle encore vides n'y est tracée que carte
        // ouverte (mosaik-render.js, edgeGuidesOn). Plier ou déplier la carte
        // change donc le dessin, pas seulement la barre latérale.
        if(id === 'mosaik-card-edges') self.syncEdgeGuides();
      });
    });

    // details drawer (the recap table) — le même bouton sur tous les écrans
    const toggle = this.$('mosaik-details-toggle');
    if(toggle) toggle.addEventListener('click', () => self.toggleDetails());

    // eraser: clicks the legacy hidden <img>, so erase mode has ONE code path —
    // y compris le fait qu'un second clic RESSORT du mode gomme (voir le
    // gestionnaire dans mosaik-controller.js)
    const er = this.$('mosaik-eraser-btn');
    if(er) er.addEventListener('click', () => {
      const legacy = self.$('mosaik-piles-remove');
      if(legacy) legacy.click();
      else if(self.m){
        if(self.m.options.editMode === 'gomme'){ self.m.exitGomme(); return; }
        self.m.rememberTool();
        self.m.options.editMode = 'gomme';
        self.markTool('gomme');
      }
    });

    // undo / redo above the plan — l'historique vit dans mosaik-toolbar.js
    document.querySelectorAll('#mosaik-stage-tools [data-act]').forEach(btn => {
      btn.addEventListener('click', () => {
        if(!window.MosaikHistory) return;
        if(btn.getAttribute('data-act') === 'undo') MosaikHistory.undo();
        else MosaikHistory.redo();
      });
    });

    // the controller announces every tool change (tile / edge colour / eraser)
    document.addEventListener('mosaik:tool', e => self.markTool(e.detail && e.detail.mode));

    this.collapseForMobile();
    this.localize();
    this.syncCart();
    this._watchResize();
    // la barre système du téléphone : armée ici, obtenue au premier geste
    this.setupFullscreen();
  },

  /* ---------- la navigation du thème, escamotée sur téléphone ----------
     La barre d'annonce et le <sticky-header> occupent le haut de l'écran en
     permanence. Sur un téléphone, c'est la hauteur du plan qu'ils prennent — et
     on vient ici pour dessiner, pas pour naviguer. Le CSS (§4) retire la barre
     d'annonce pour de bon et sort l'en-tête du flux, remonté hors champ ; ce qui
     suit dit QUAND rappeler ce dernier : quand le visiteur remonte, comme le
     ferait n'importe quel en-tête collant.

     TROIS DÉFILEMENTS, DONT UN QUI N'EN EST PAS UN.
       - la page : le pied de page du thème vit sous le shell, elle défile donc ;
       - la fenêtre des réglages : c'est LE défilement de cette page (§4) ;
       - et le cas où il n'y en a aucun : liste en haut, page en haut, plus rien
         à faire remonter. Aucun événement de défilement ne partira jamais, et
         sans le geste ci-dessous l'en-tête serait tout simplement inatteignable
         pour qui n'a touché à rien — on écoute donc le doigt qui tire vers le
         bas en haut de la liste.

     Câblé sur tous les écrans, et non sous condition de largeur : les écouteurs
     sont passifs et ne coûtent rien, le CSS ne s'applique qu'en dessous de
     768 px, et un téléphone qui pivote n'a rien à recâbler. */
  NAV_DELTA: 6,          // px de remontée avant de rappeler l'en-tête
  NAV_PULL: 40,          // px de doigt, liste en butée haute, pour le rappeler
  _navReady: false,
  setupNavAway:function(){
    if(this._navReady) return;
    const root = document.documentElement;
    // pas d'en-tête de thème (aperçu local, autre gabarit) : rien à escamoter
    if(!document.querySelector('.shopify-section-header')) return;
    this._navReady = true;
    const self = this;
    root.classList.add('mosaik-nav-away');
    const show = on => root.classList.toggle('mosaik-nav-shown', on);
    // remonter, c'est demander l'en-tête ; redescendre, c'est le renvoyer.
    // Le seuil évite qu'un pixel de ballotement le fasse clignoter.
    const watch = read => {
      let last = read();
      return () => {
        const y = read(), d = y - last;
        last = y;
        if(d < -self.NAV_DELTA) show(true);
        else if(d > self.NAV_DELTA) show(false);
      };
    };
    window.addEventListener('scroll', watch(() => window.pageYOffset || 0), { passive:true });
    const side = this.$('mosaik-sidebar');
    if(side){
      side.addEventListener('scroll', watch(() => side.scrollTop), { passive:true });
      let y0 = null;
      side.addEventListener('touchstart', e => {
        y0 = (side.scrollTop === 0 && e.touches[0]) ? e.touches[0].clientY : null;
      }, { passive:true });
      side.addEventListener('touchmove', e => {
        if(y0 === null || !e.touches[0]) return;
        if(e.touches[0].clientY - y0 > self.NAV_PULL){ show(true); y0 = null; }
      }, { passive:true });
    }
    // dessiner, c'est en avoir fini avec la navigation : elle couvre le haut du plan
    const stage = this.$('mosaik-stage');
    if(stage) stage.addEventListener('pointerdown', () => show(false), { passive:true });
  },

  /* ---------- la barre du téléphone, escamotée elle aussi ----------
     La barre système du bas (retour / accueil / applications récentes) mange
     le bas de l'écran en permanence, et sur cette page-ci elle fait pire : on
     dessine au doigt juste au-dessus, et un geste qui déborde d'un centimètre
     quitte la page — la création avec.

     LE PLEIN ÉCRAN EST LA SEULE PRISE. Aucune propriété CSS ne retire cette
     barre ; l'API Fullscreen, elle, fait passer le navigateur en mode immersif,
     et la barre système s'efface avec la barre d'état. `navigationUI:'hide'`
     est la façon de le DEMANDER explicitement (les navigateurs qui l'ignorent
     escamotent quand même).

     « SELON LES MODÈLES », ET C'EST VOULU. iOS ne connaît requestFullscreen que
     sur une vidéo : la détection ci-dessous n'y trouve rien, ne tente rien, et
     la page marche exactement comme avant. Android l'applique.

     UNE SEULE DEMANDE, AU PREMIER GESTE. Le plein écran ne s'obtient que depuis
     un geste du visiteur — impossible au chargement. On l'attend donc, une fois,
     puis on ne redemande JAMAIS : ressortir du plein écran (glissement depuis
     le haut) est une réponse, et la reposer à chaque appui serait un piège.
     Le premier geste est le plus souvent la validation du popup de départ, donc
     juste avant de dessiner : c'est le bon moment.

     Le passage en plein écran change la hauteur de la fenêtre, donc déclenche
     `resize` : _watchResize refait la mise en page et redessine le plan à la
     taille gagnée. Rien à faire de plus ici. */
  _fsAsked: false,
  setupFullscreen:function(){
    const root = document.documentElement;
    const req = root.requestFullscreen || root.webkitRequestFullscreen;
    if(!req) return;                          // iOS & compagnie : on passe notre tour
    const conf = this.$('mosaik-conf');
    if(!conf) return;
    const self = this;
    const ask = function(){
      if(!self.isMobile()) return;            // au bureau il n'y a pas de barre à retirer
      conf.removeEventListener('touchend', ask);
      conf.removeEventListener('click', ask);
      if(self._fsAsked) return;
      self._fsAsked = true;
      if(document.fullscreenElement || document.webkitFullscreenElement) return;
      try {
        const r = req.call(root, { navigationUI:'hide' });
        // refus (réglage du navigateur, iframe sans autorisation) : sans effet,
        // et surtout sans promesse non rattrapée dans la console
        if(r && r.catch) r.catch(function(){});
      } catch(e){ /* le configurateur marche sans : on n'insiste pas */ }
    };
    conf.addEventListener('touchend', ask, { passive:true });
    conf.addEventListener('click', ask);
  },

  /* ---------- l'état de départ sur un téléphone ----------
     L'écran y est partagé : le plan en haut, la fenêtre des réglages au milieu,
     la barre d'action en bas (§4 du CSS). Cette fenêtre est courte — toutes les
     cartes ouvertes, « Dalles », la seule dont on ait besoin pour commencer, se
     retrouve à deux écrans de défilement du départ. On n'ouvre donc que celle-là
     et on cale la fenêtre dessus : le visiteur voit son plan ET son nuancier
     sans avoir rien fait.

     Le repli n'est posé qu'AU DÉMARRAGE : les cartes se rouvrent et se referment
     ensuite comme partout ailleurs. Et il est posé ICI plutôt qu'en `collapsed`
     dans le markup, qui vaudrait aussi pour le bureau — celui-ci a la place de
     tout montrer, et le visiteur y voit d'un coup d'œil ce qu'il peut régler. */
  collapseForMobile:function(){
    if(!this.isMobile()) return;
    const open = this.$('mosaik-card-tiles');
    document.querySelectorAll('#mosaik-sidebar .mosaik-card').forEach(c => {
      c.classList.toggle('collapsed', c !== open);
    });
  },

  /* Cale « Dalles » en tête de la fenêtre des réglages, UNE fois — et seulement
     quand cette fenêtre déborde vraiment. Tant que le premier plan n'est pas
     dessiné, la barre latérale est encore plus haute que son contenu : un
     scrollTop écrit là serait ramené à 0 par le navigateur et la place serait
     perdue. Appelée à chaque recalcul (donc après chaque dessin), elle attend la
     mise en page réelle, se fait, puis se retire. */
  _tilesPlaced: false,
  placeTilesCard:function(){
    if(this._tilesPlaced || !this.isMobile()) return;
    const side = this.$('mosaik-sidebar'), card = this.$('mosaik-card-tiles');
    if(!side || !card) return;
    if(side.scrollHeight <= side.clientHeight + 4) return;   // rien ne défile : plus tard
    this._tilesPlaced = true;
    const d = card.getBoundingClientRect().top - side.getBoundingClientRect().top;
    side.scrollTop = Math.max(0, side.scrollTop + d - 4);
  },

  localize:function(){
    const m = this.m; if(!m) return;
    const set = (id, s) => { const el = this.$(id); if(el) el.innerText = m.getText(s); };
    set('mosaik-card-tiles-title', 'Tiles');
    set('mosaik-card-edges-title', 'Borders and corners');
    set('mosaik-edges-hint',       'Pick a colour, then click an element of the plan on the left.');
    set('mosaik-card-objects-title', window.MOSAIK_ACCESSORIES_ENABLED === true
      ? 'Parking spaces and accessories' : 'Parking spaces');
    set('mosaik-card-model-title', 'Your model');
    set('mosaik-card-dims-title',  'Your overall dimensions');
    set('mosaik-card-space-title', 'Adjust your space');
    set('mosaik-eraser-label',     'Remove / Add tiles');
    // l'outil main, quand il est armé : le libellé de la pastille et la mention
    // qui dit ce qu'il empêche (mosaik-zoom.js les fait paraître — voir dSync)
    set('mosaik-pan-label',        'Move');
    set('mosaik-pan-note',         'Drawing paused');
    // la bibliothèque de modèles est une carte de la barre latérale (plus un popup)
    set('mosaik-card-templates-title', 'Browse templates');
    // même libellé que le pied du récap (tiroir « Afficher les détails ») : c'est
    // le même montant, il ne doit pas s'appeler autrement selon l'endroit
    set('mosaik-bar-total-label',  'Price including tax');
    const det = this.$('mosaik-details');
    set('mosaik-details-label', (det && !det.hidden) ? 'Details' : 'Details');
    const undo = document.querySelector('#mosaik-stage-tools [data-act="undo"]'),
          redo = document.querySelector('#mosaik-stage-tools [data-act="redo"]');
    if(undo){ undo.title = m.getText('Undo'); undo.setAttribute('aria-label', m.getText('Undo')); }
    if(redo){ redo.title = m.getText('Redo'); redo.setAttribute('aria-label', m.getText('Redo')); }
    // zoom toolbar over the plan (mosaik-zoom.js owns the behaviour)
    const ZOOM = { pan:'Move the plan', out:'Zoom out', in:'Zoom in', fit:'Fit to view' };
    Object.keys(ZOOM).forEach(k => {
      const b = document.querySelector('#mosaik-zoom-tools [data-zoom="' + k + '"]');
      if(b){ b.title = m.getText(ZOOM[k]); b.setAttribute('aria-label', m.getText(ZOOM[k])); }
    });
  },

  /* Déplie une carte de la barre latérale sans toucher aux autres. Appelée de
     l'extérieur — mosaik-select.js ouvre « Dalles » quand une zone vient d'être
     tracée. */
  openCard:function(id){
    const card = this.$(id);
    if(!card) return;
    const was = card.classList.contains('collapsed');
    card.classList.remove('collapsed');
    if(was && id === 'mosaik-card-edges') this.syncEdgeGuides();
  },

  /* Repasse sur le plan après un pliage / dépliage de « Bordures et angles » :
     la délimitation des cases de bordure et d'angle encore vides n'y est tracée
     que carte ouverte (mosaik-render.js, edgeGuidesOn), il faut donc redessiner
     pour la faire paraître ou disparaître.

     C'est bien un draw() complet — le seul qui sache reposer les dalles, les
     bordures colorées et les calques par-dessus. La géométrie, elle, ne change
     pas d'un pli à l'autre : le plan garde sa taille, et le zoom en cours (une
     transformation CSS sur le canvas) lui survit. */
  syncEdgeGuides:function(){
    if(this.m) this.m.draw();
  },

  /* ---------- details drawer ----------
     Sur grand écran ET sur téléphone, le tiroir flotte juste au-dessus de la
     barre d'action : il s'ouvre sous les yeux. Reste la tablette empilée, où il
     est POSÉ APRÈS elle, donc hors de l'écran — on l'y amène, sinon le bouton
     semble sans effet. */
  toggleDetails:function(open){
    const det = this.$('mosaik-details'), btn = this.$('mosaik-details-toggle');
    if(!det) return;
    const show = (open === undefined) ? det.hidden : !!open;
    det.hidden = !show;
    if(btn) btn.classList.toggle('open', show);
    const lbl = this.$('mosaik-details-label');
    if(lbl && this.m) lbl.innerText = this.m.getText(show ? 'Details' : 'Details');
    if(show && !this.isSplit() && !this.isMobile() && det.scrollIntoView)
      det.scrollIntoView({ block:'nearest', behavior:'smooth' });
  },

  /* ---------- live total (called by mosaik-recap.js) ----------
     Le bandeau est désormais le SEUL endroit où le prix TTC s'affiche à l'écran
     (le récap du tiroir ne le répète plus, voir mosaik-recap.js) : il doit donc
     rester lisible en permanence, y compris à 0 € tant qu'aucune dalle n'est
     posée — un « — » laissait croire que le prix n'était pas calculé.

     Seule Aero (Polypropylene) porte des tarifs ; sur les autres collections il
     n'y a rien à totaliser, alors on masque le bloc entier plutôt que d'annoncer
     un 0,00 € faux. `this.m` peut encore être nul au tout premier dessin (init()
     dessine avant setupUI), d'où le repli sur window.mosaik. */
  onRecalc:function(total){
    const m   = this.m || window.mosaik,
          box = document.querySelector('#mosaik-actionbar .mosaik-bar-total'),
          el  = this.$('mosaik-bar-total-val');
    const priced = !!(m && m.options && m.options.currentCollection === 'Polypropylene');
    if(box) box.style.display = priced ? '' : 'none';
    if(el)  el.innerText = (priced && typeof total === 'number' && isFinite(total))
              ? total.toFixed(2) + ' €' : '—';
    this.syncCart();
    // le plan vient d'être dessiné, donc la fenêtre des réglages a sa taille
    // définitive : c'est le moment de la caler sur « Dalles » (une seule fois)
    this.placeTilesCard();
  },

  /* Only the Aero collection carries cart variant ids; elsewhere the controller
     hides the button entirely (historical behaviour) — nothing to do here beyond
     keeping the two in sync when a collection has no price at all. */
  syncCart:function(){
    const b = this.$('mosaik-add-to-cart');
    if(!b || !this.m) return;
    b.disabled = this.m.options.currentCollection !== 'Polypropylene';
  },

  /* ---------- selection feedback ----------
     `mode` is the engine's editMode: dalles | contour | bordures | angles | gomme */
  markTool:function(mode){
    const er = this.$('mosaik-eraser-btn');
    // la gomme s'allume ET s'éteint (second clic) : c'est un interrupteur, donc
    // aria-pressed en plus de la classe visuelle
    if(er){
      er.classList.toggle('sel', mode === 'gomme');
      er.setAttribute('aria-pressed', mode === 'gomme' ? 'true' : 'false');
    }
    if(mode !== 'contour')
      document.querySelectorAll('#mosaik-edges-list .sel').forEach(el => el.classList.remove('sel'));
    if(mode !== 'dalles')
      document.querySelectorAll('#mosaik-sidebar #mosaik-piles img.sel').forEach(el => el.classList.remove('sel'));
  },

  /* Hide a sidebar card whose palette is empty for the active collection
     (called by the controller after every collection change). */
  refreshCards:function(){
    const m = this.m; if(!m) return;
    const col = m.collections[m.options.currentCollection] || {};
    const edges = ((col.borders || []).length + (col.corners || []).length) > 0;
    const card = this.$('mosaik-card-edges');
    if(card) card.style.display = edges ? '' : 'none';
    this.syncCart();
  }
};
